import { createSlice } from "@reduxjs/toolkit";
import {
  getStudyNote,
  saveStudyNote,
  deleteStudyNote,
  getStudyNoteTickers,
  getWatchlist,
  addToWatchlist,
  removeFromWatchlist,
} from "./thunks";
import { logout, expiredLogout } from "../auth/thunks";

const initialState = {
  // Map of ticker -> note row ({ study_note_id, content, ... }) or null when
  // the user has no note yet for that ticker. Keyed by ticker so deep-linked
  // pages don't churn the cache as users flip symbols.
  notes: {},
  // Lightweight set of tickers the user has saved a note for. Drives the
  // "📝 has note" indicator across pages without per-row fetches.
  noteTickers: [],
  watchlist: [],
  // Loaded flags prevent re-fetch loops on pages whose layout swaps in/out of
  // a Loading skeleton (e.g. Home wraps content in AnimatePresence keyed on
  // ui.loading). Each dispatch flips ui.loading, which would otherwise unmount
  // and remount the consumer, re-firing the on-mount fetch in a loop.
  // Reset to false via `() => initialState` on logout.
  watchlistLoaded: false,
  noteTickersLoaded: false,
};

const addTickerToList = (list, ticker) => {
  if (!ticker) return list;
  if ((list || []).includes(ticker)) return list;
  return [...(list || []), ticker].sort();
};

const removeTickerFromList = (list, ticker) => {
  if (!ticker) return list;
  return (list || []).filter((t) => t !== ticker);
};

const upsertWatchlistItem = (list, item) => {
  if (!item) return list;
  const others = (list || []).filter((row) => row.ticker_name !== item.ticker_name);
  return [...others, item].sort((a, b) => a.ticker_name.localeCompare(b.ticker_name));
};

const studySlice = createSlice({
  name: "study",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStudyNote.fulfilled, (state, action) => {
        const ticker = action.payload?.ticker_name;
        if (ticker) {
          state.notes[ticker] = action.payload?.note ?? null;
        }
      })
      .addCase(saveStudyNote.fulfilled, (state, action) => {
        const ticker = action.payload?.ticker_name;
        if (ticker) {
          state.notes[ticker] = action.payload?.note ?? null;
          if (action.payload?.note?.content) {
            state.noteTickers = addTickerToList(state.noteTickers, ticker);
          }
        }
      })
      .addCase(deleteStudyNote.fulfilled, (state, action) => {
        const ticker = action.payload?.ticker_name;
        if (ticker) {
          state.notes[ticker] = null;
          state.noteTickers = removeTickerFromList(state.noteTickers, ticker);
        }
      })
      .addCase(getStudyNoteTickers.fulfilled, (state, action) => {
        state.noteTickers = action.payload?.tickers_with_notes || [];
        state.noteTickersLoaded = true;
      })
      .addCase(getWatchlist.fulfilled, (state, action) => {
        state.watchlist = action.payload?.watchlist || [];
        state.watchlistLoaded = true;
      })
      .addCase(addToWatchlist.fulfilled, (state, action) => {
        state.watchlist = upsertWatchlistItem(state.watchlist, action.payload?.watchlist_item);
      })
      .addCase(removeFromWatchlist.fulfilled, (state, action) => {
        const ticker = action.payload?.ticker_name || action.payload?.removed_ticker;
        if (ticker) {
          state.watchlist = (state.watchlist || []).filter(
            (row) => row.ticker_name !== ticker
          );
        }
      })
      .addCase(logout.fulfilled, () => initialState)
      .addCase(expiredLogout.fulfilled, () => initialState);
  },
});

export default studySlice.reducer;
